import { useEffect, useRef, useState } from 'react';
import type { Listing, SimplePaginator } from 'shared/@types';
import { useLocale } from 'shared/library/i18n/use-locale';
import useApi from 'shared/library/use-api';
import { apiRoute } from 'shared/library/api-route';

export function useSimilarListings(listingSlug: string): Listing[] {
  const { languageCode, countryCode } = useLocale();
  const { request } = useApi({ notifyOnServerErrors: false });
  const requestRef = useRef(request);
  requestRef.current = request;
  const key = JSON.stringify([listingSlug, languageCode, countryCode]);
  const [result, setResult] = useState<{ key: string; listings: Listing[] }>();

  useEffect(() => {
    const controller = new AbortController();

    requestRef
      .current<{ similar_listings: SimplePaginator<Listing> }>(
        apiRoute('listings.similar', { listing: listingSlug }),
        { format: 'card' },
        undefined,
        controller,
      )
      .then((response) => {
        if (!controller.signal.aborted) {
          setResult({ key, listings: response.similar_listings.data });
        }
      })
      .catch(() => {
        // Recommendations are optional; the listing remains usable if they fail.
      });

    return () => controller.abort();
  }, [listingSlug, languageCode, countryCode, key]);

  return result?.key === key ? result.listings : [];
}
